import { numFont } from '../../../utils/fonts';
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Checkbox,
  Divider,
  TextField,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FormControlLabel } from '@mui/material';
import DecimalField from '../../shared/DecimalField';
import TrialFeatureNotice from '../../shared/TrialFeatureNotice';
import { PartSectionHeader, PartCandidateRow } from '../../shared/PartSourceSections';
import { formatPriceWithCurrency } from '../../../utils/vat';

// The same source-sectioning language as the OEM "Mistä osa otetaan?" picker: every candidate
// is filed under WHERE it comes from, with a plain-language line on why it should fit.
const PART_SECTIONS = [
  { key: 'registry', sources: ['registry'], Icon: InventoryOutlinedIcon },
  { key: 'priced', sources: ['catalogue', 'supplier'], Icon: MenuBookOutlinedIcon },
  { key: 'kb', sources: ['kb', 'advisory', 'not_found'], Icon: AutoAwesomeOutlinedIcon },
];

// Model-level (rung >= 4) suggestions pool across generations and engines — real data, high
// wrong-part risk. They are opt-in via the "Näytä epävarmat osaehdotukset" toggle.
const isUncertainOp = (op) => (op.kb?.rung ?? 0) >= 4;

function AIWorkOrderDialog({
  t,
  open,
  vehicle,
  customer,
  initialText,
  addCreatedMaintenance,
  handleClose,
  // Calendar context (optional): schedule the created work order into a slot and
  // hand off via onCreated instead of navigating to the work order page.
  // scheduleContext: { startDateTime, endDateTime, serviceBayId, employeeIds }
  scheduleContext = null,
  onCreated = null,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [ops, setOps] = useState([]);
  const [creating, setCreating] = useState(false);
  const [noPartsWarning, setNoPartsWarning] = useState(false);
  const [showUncertain, setShowUncertain] = useState(false);
  const navigate = useNavigate();

  const toggleUncertain = () => {
    setShowUncertain((prev) => {
      const next = !prev;
      if (!next) {
        // Hiding the uncertain tier must also unselect it — a hidden selected part on the
        // created work order would be exactly the silent wrong-part risk the gate exists for.
        setOps((old) => old.map((op) => (
          isUncertainOp(op) ? { ...op, parts: op.parts.map((p) => ({ ...p, selected: false })) } : op
        )));
      }
      return next;
    });
  };

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.post('/api/ai-work-order/preview/', {
        vehicle_id: vehicle.id || null,
        registration_number: vehicle.registration_number,
        text: initialText,
      });
      setSuggestion(resp.data);
      setOps((resp.data.operations || []).map((op) => ({
        ...op,
        include: true,
        job: { ...op.job },
        parts: (op.parts || []).map((p) => ({ ...p })),
      })));
    } catch (e) {
      setError(e.response?.data?.error || t('aiWorkOrder.previewFailed'));
    } finally {
      setLoading(false);
    }
  }, [vehicle, initialText, t]);

  useEffect(() => {
    if (open && (vehicle?.id || vehicle?.registration_number) && initialText) {
      fetchPreview();
    }
    if (!open) {
      setSuggestion(null);
      setOps([]);
      setError(null);
      setCreating(false);
    }
  }, [open, vehicle, initialText, fetchPreview]);

  const patchOp = (i, patch) => setOps((prev) => prev.map((op, idx) => (idx === i ? { ...op, ...patch } : op)));
  const patchPart = (i, j, patch) => {
    setNoPartsWarning(false); // selection changed — re-arm the no-parts guard
    setOps((prev) => prev.map((op, idx) => {
      if (idx !== i) return op;
      // Radio-within-role: selecting an exclusive-role candidate (two brands of the same part)
      // deselects its same-role siblings so duplicates can't be added by accident. Companion
      // roles (seals, bulbs, wipers…) are non-exclusive and keep normal checkbox behavior.
      const target = op.parts[j];
      const takeover = patch.selected === true && target.role && target.role_exclusive;
      return {
        ...op,
        parts: op.parts.map((p, jj) => {
          if (jj === j) return { ...p, ...patch };
          if (takeover && p.role === target.role && p.selected) return { ...p, selected: false };
          return p;
        }),
      };
    }));
  };

  // Backend orders candidates so same-role rows are adjacent — group consecutive runs.
  // Takes [part, originalIndex] pairs so section filtering upstream keeps patchPart indices.
  const roleGroups = (pairs) => {
    const groups = [];
    pairs.forEach(([p, j]) => {
      const last = groups[groups.length - 1];
      if (p.role && last && last.role === p.role) last.items.push([p, j]);
      else groups.push({ role: p.role || '', exclusive: !!p.role_exclusive, items: [[p, j]] });
    });
    return groups;
  };

  const selectedParts = ops.filter((op) => op.include).flatMap((op) => op.parts.filter((p) => p.selected));
  const partsSubtotal = selectedParts.reduce(
    (sum, p) => sum + (parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1), 0);
  // Candidates the user COULD have picked (advisory rows can't be purchased and parts hidden
  // behind the uncertain toggle can't be seen, so neither counts toward the no-parts guard)
  const hasSelectableCandidates = ops
    .filter((op) => op.include && (showUncertain || !isUncertainOp(op)))
    .some((op) => op.parts.some((p) => p.source !== 'advisory'));
  const hasUncertainParts = ops.some((op) => isUncertainOp(op) && op.parts.length > 0);

  const handleCreate = async () => {
    // Choice-mode trap guard: candidates exist but none picked — creating silently produces a
    // partless work order. Ask once; a second click (or "Luo ilman osia") proceeds.
    if (selectedParts.length === 0 && hasSelectableCandidates && !noPartsWarning) {
      setNoPartsWarning(true);
      return;
    }
    setCreating(true);
    setError(null);
    const included = ops.filter((op) => op.include);
    const jobs = included.map((op) => ({ description: op.job.description, hours: op.job.hours, ai_estimation: {} }));
    const parts = [];
    included.forEach((op) =>
      op.parts
        .filter((p) => p.selected)
        .forEach((p) => parts.push({
          part_id: p.part_id, code: p.code, ean: p.ean, name: p.name, brand: p.brand,
          price: p.price, cost: p.cost, quantity: p.quantity || 1, source: p.source,
        })));
    try {
      const resp = await axios.post('/api/ai-work-order/create/', {
        vehicle_id: vehicle.id || null,
        registration_number: vehicle.registration_number,
        customer_id: customer?.id || null, jobs, parts, description: initialText,
        suggestion_id: suggestion?.suggestion_id || null,
      });

      // Schedule into the calendar slot when opened with one
      let schedule = null;
      let scheduleFailed = false;
      if (scheduleContext?.startDateTime && scheduleContext?.endDateTime && resp.data?.id) {
        const employeeIds = scheduleContext.employeeIds || [];
        try {
          const scheduleResponse = await axios.post(`/api/maintenances/${resp.data.id}/schedule/`, {
            start_time: scheduleContext.startDateTime.toISOString(),
            end_time: scheduleContext.endDateTime.toISOString(),
            service_bay_id: scheduleContext.serviceBayId || null,
            // The AI create endpoint assigns no employees, so these must be passed here.
            ...(employeeIds.length > 0
              ? { employee_ids: employeeIds, primary_employee_id: employeeIds[0] }
              : {}),
          });
          // A freshly created work order has exactly one schedule.
          schedule = scheduleResponse.data?.schedules?.[0] || null;
        } catch (scheduleError) {
          console.error('Error scheduling the created work order:', scheduleError);
          scheduleFailed = true;
        }
      }

      addCreatedMaintenance?.(resp.data);
      if (onCreated) {
        onCreated({
          maintenance: resp.data,
          schedule,
          scheduleFields: scheduleContext,
          warnings: { scheduleFailed, oemAttachFailed: false },
        });
        handleClose();
        return;
      }
      handleClose();
      navigate(`/maintenances/${resp.data.id}`);
    } catch (e) {
      setError(e.response?.data?.error || t('aiWorkOrder.createFailed'));
      setCreating(false);
    }
  };

  // Complete transparency on the matching grounds: the chip names the tier, the tooltip
  // spells out exactly which vehicle attributes the suggestions are pooled on — the user
  // must never have to guess what "Malli-taso" means.
  const precisionChip = (kb) => {
    if (!kb) return <Chip size="small" variant="outlined" label={t('aiWorkOrder.noData')} />;
    const label = t(`aiWorkOrder.precision.${kb.precision}`);
    return (
      <Tooltip title={t(`aiWorkOrder.precisionHelp.${kb.precision}`)} arrow enterTouchDelay={0}>
        <Chip
          size="small"
          data-testid="ai-precision-chip"
          color={kb.trusted ? 'success' : 'default'}
          variant={kb.trusted ? 'filled' : 'outlined'}
          icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
          label={`${label} · ${kb.n_vehicles} ${t('aiWorkOrder.cars')}`}
        />
      </Tooltip>
    );
  };

  const partSecondary = (p) => {
    const base = [p.brand, p.code, p.vendor].filter(Boolean).join(' · ');
    if (!p.kb_freq) return base || null;
    return (
      <>
        {base}
        <Tooltip title={t('aiWorkOrder.freqTooltip', { n: p.kb_freq })} arrow>
          <span>{base ? ' · ' : ''}{p.kb_freq}×</span>
        </Tooltip>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth data-testid="ai-work-order-dialog">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeOutlinedIcon sx={{ color: 'secondary.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{t('aiWorkOrder.dialogTitle')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[vehicle?.manufacturer, vehicle?.model].filter(Boolean).join(' ')} {vehicle?.registration_number}
          </Typography>
        </Box>
        {scheduleContext && (
          <Chip size="small" variant="outlined" color="secondary" label={t('aiWorkOrder.willSchedule')} sx={{ mr: 1 }} />
        )}
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TrialFeatureNotice />
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
            <CircularProgress />
            <Typography color="text.secondary">{t('aiWorkOrder.generating')}</Typography>
          </Box>
        )}

        {!loading && error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>}

        {!loading && suggestion && (
          <>
            {(suggestion.warnings || []).includes('no_operations') && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>{t('aiWorkOrder.noOperations')}</Alert>
            )}
            {(suggestion.warnings || []).includes('no_hourly_job') && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>{t('aiWorkOrder.noHourlyJob')}</Alert>
            )}
            {suggestion.clarify && suggestion.clarify_question && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>{suggestion.clarify_question}</Alert>
            )}

            {hasUncertainParts && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showUncertain}
                      onChange={toggleUncertain}
                      data-testid="ai-show-uncertain"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      {t('aiWorkOrder.showUncertain')}
                    </Typography>
                  }
                />
                <Tooltip title={t('aiWorkOrder.showUncertainHelp')} arrow enterTouchDelay={0}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </Tooltip>
              </Box>
            )}

            {ops.map((op, i) => (
              <Box key={i} sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider', opacity: op.include ? 1 : 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Checkbox checked={op.include} onChange={() => patchOp(i, { include: !op.include })} sx={{ p: 0.5 }} />
                  <BuildOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <TextField
                    variant="standard"
                    fullWidth
                    value={op.job.description}
                    onChange={(e) => patchOp(i, { job: { ...op.job, description: e.target.value } })}
                    InputProps={{ disableUnderline: false, sx: { fontWeight: 600 } }}
                  />
                  {precisionChip(op.kb)}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('aiWorkOrder.hours')}</Typography>
                  <DecimalField
                    value={op.job.hours}
                    onChange={(v) => patchOp(i, { job: { ...op.job, hours: v } })}
                    size="small"
                    sx={{ width: '90px' }}
                  />
                  <Chip size="small" variant="outlined" label={t(`aiWorkOrder.hoursSource.${op.job.hours_source || 'default'}`)} />
                </Box>

                {op.parts.length > 0 && isUncertainOp(op) && !showUncertain ? (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ pl: 5, display: 'block' }}
                      data-testid="ai-uncertain-hidden"
                    >
                      {t('aiWorkOrder.uncertainHidden', { n: op.parts.length })}
                    </Typography>
                  </>
                ) : (
                  <>
                    {op.parts.length > 0 && !op.parts.some((p) => p.selected) && (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 5, display: 'block' }}>
                        {t('aiWorkOrder.choiceHint')}
                      </Typography>
                    )}

                    {op.parts.length > 0 && <Divider sx={{ my: 1 }} />}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 5 }}>
                      {PART_SECTIONS.map(({ key, sources, Icon }) => {
                        const sectionParts = op.parts
                          .map((p, j) => [p, j])
                          .filter(([p]) => sources.includes(p.source));
                        if (sectionParts.length === 0) return null;
                        return (
                          <Box key={key}>
                            <PartSectionHeader
                              icon={<Icon sx={{ fontSize: 16, color: 'secondary.main' }} />}
                              title={t(`aiWorkOrder.section.${key}`)}
                              help={t(`aiWorkOrder.sectionHelp.${key}`)}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              {roleGroups(sectionParts).map((group, g) => (
                                <Box key={g} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {group.role && group.items.length > 1 && (
                                    <Typography
                                      variant="caption"
                                      sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}
                                      data-testid="ai-part-role-header"
                                    >
                                      {t(`aiWorkOrder.role.${group.role}`, { defaultValue: t('aiWorkOrder.role.other') })}
                                      {group.exclusive && (
                                        <Typography component="span" variant="caption" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                                          {' — '}{t('aiWorkOrder.pickOne')}
                                        </Typography>
                                      )}
                                    </Typography>
                                  )}
                                  {group.items.map(([p, j]) => {
                                    return (
                                      <PartCandidateRow
                                        key={j}
                                        disabled={p.source === 'advisory'}
                                        primary={p.name || p.code}
                                        secondary={partSecondary(p)}
                                        leading={
                                          <Checkbox
                                            checked={!!p.selected}
                                            disabled={p.source === 'advisory'}
                                            onChange={() => patchPart(i, j, { selected: !p.selected })}
                                            sx={{ p: 0.5 }}
                                          />
                                        }
                                        trailing={
                                          <>
                                            {p.selected && (
                                              <DecimalField value={p.quantity} onChange={(v) => patchPart(i, j, { quantity: v })} size="small" sx={{ width: '70px' }} />
                                            )}
                                            <Typography variant="body2" sx={{ minWidth: 70, textAlign: 'right', fontWeight: 600, ...numFont, fontSize: 13 }}>
                                              {p.price != null ? formatPriceWithCurrency(p.price) : '—'}
                                            </Typography>
                                          </>
                                        }
                                      />
                                    );
                                  })}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        {noPartsWarning && selectedParts.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }} data-testid="ai-wo-no-parts-warning">
            {t('aiWorkOrder.noPartsWarning')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {selectedParts.length > 0
              ? <>{t('aiWorkOrder.partsSubtotal')}: <strong>{formatPriceWithCurrency(partsSubtotal)}</strong></>
              : t('aiWorkOrder.noPartsSelected')}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '10px' }}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            data-testid="ai-wo-create"
            onClick={handleCreate}
            disabled={loading || creating || !ops.some((op) => op.include)}
            startIcon={creating ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
            sx={{ borderRadius: '10px', bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.light' } }}
          >
            {noPartsWarning && selectedParts.length === 0
              ? t('aiWorkOrder.createAnyway')
              : t('aiWorkOrder.createWorkOrder')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(AIWorkOrderDialog);
